import { camelCase } from 'lodash';

import { SortOrder } from 'components/insights/constants';
import { PortfolioTab } from 'components/portfolio/Portfolio';
import {
  ADDITIONAL_FILTERS,
  CASE_MANAGER_DROPDOWN,
  COMANAGEMENT_TEAM,
  createClientDropdown,
  createGroupTypeDropdown,
  DISCHARGE_REASON_DROPDOWN,
  EPISODE_TYPE_DROPDOWN,
  getComanagementTeamManagerFilterDropdowns,
  ONLY_OTHER_ORGANIZATIONS_CHECKBOX,
  PLAN_TYPE_DROPDOWN,
  REMOVED_FROM_CENSUS_CHECKBOX,
  sortFilterDropdowns,
  STATUS_OUT_OF_DATE_CHECKBOX,
  UTILIZATION_MANAGER_DROPDOWN,
} from 'constants/filterConfigs';
import {
  ONLY_OTHER_ORGANIZATIONS,
  OWNER_CLIENT,
  PatientState,
  REMOVED_FROM_CENSUS,
  STATUS_OUT_OF_DATE,
} from 'constants/filterKeysConstants';
import { defaultTranslate, TranslateFnType } from 'context/translations';
import { ImportType } from 'models/Client';
import FilterValueArray from 'models/filterValues/FilterValueArray';
import FilterValueBoolean from 'models/filterValues/FilterValueBoolean';
import GroupType from 'models/GroupType';
import Profile from 'models/Profile';
import RehabState, { RehabStateApiName } from 'models/RehabState';
import { PortfolioFilterState } from 'stores/portfolioStore';

const getAdditionalFilterDropdowns = (
  selectedTab: PortfolioTab,
  profile: Profile,
  translate: TranslateFnType = defaultTranslate
) => {
  const { caseManager: caseManagerEnabled, utilizationManager: utilizationManagerEnabled } =
    profile.actingClient.configForManagedGroupType(selectedTab?.value ?? '') ?? {};

  return [
    // dropdown filters
    ...(caseManagerEnabled ? [CASE_MANAGER_DROPDOWN] : []),
    {
      ...DISCHARGE_REASON_DROPDOWN,
      placeholder: `${translate('Discharged')} To`,
    },
    EPISODE_TYPE_DROPDOWN,
    PLAN_TYPE_DROPDOWN,
    ...(utilizationManagerEnabled ? [UTILIZATION_MANAGER_DROPDOWN] : []),
    // checkbox filters
    ...(profile.isProviderForClientManagedAndProvidedGroupType(selectedTab?.groupType ?? '')
      ? [ONLY_OTHER_ORGANIZATIONS_CHECKBOX]
      : []),
    ...(profile.actingClient.managesAndProvidesGroupType(selectedTab.groupType || '') &&
    profile.actingClient.importType == ImportType.PATIENT
      ? [REMOVED_FROM_CENSUS_CHECKBOX]
      : []),
    ...(selectedTab.patientState !== PatientState.CONTINUED ? [STATUS_OUT_OF_DATE_CHECKBOX] : []),
  ];
};

export const getFilterSections = (selectedTab: PortfolioTab, profile: Profile, translate: TranslateFnType) => {
  return [
    {
      title: COMANAGEMENT_TEAM,
      filters: sortFilterDropdowns([
        ...(profile.canActAsProvider
          ? [createClientDropdown([], 'Organization', OWNER_CLIENT, { viewOnly: false, groupTypeType: 'manager' })]
          : []),

        ...(![PatientState.CONTINUED, PatientState.LATEST].includes(selectedTab.patientState as PatientState)
          ? [
              createGroupTypeDropdown(
                new GroupType({
                  id: selectedTab?.groupType ?? '',
                  displayName: selectedTab?.displayName ?? '',
                  apiName: selectedTab?.value ?? '',
                }),
                {
                  include: 'groupType',
                }
              ),
            ]
          : []),

        ...getComanagementTeamManagerFilterDropdowns(profile),
      ]),
    },
    {
      title: ADDITIONAL_FILTERS,
      filters: getAdditionalFilterDropdowns(selectedTab, profile, translate),
    },
  ];
};

export function filtersForSelectedTab(
  filters: PortfolioFilterState,
  tab: PortfolioTab,
  profile: Profile
): PortfolioFilterState {
  if (!tab) return {};

  return Object.entries(filters).reduce((acc, [filterKey, value]) => {
    if (value instanceof FilterValueArray && !value.length) return acc;

    const { caseManager: caseManagerEnabled, utilizationManager: utilizationManagerEnabled } =
      profile.actingClient.configForManagedGroupType(tab?.value ?? '') ?? {};

    if (filterKey === 'caseManager' && !caseManagerEnabled) return acc;
    if (filterKey === 'utilizationManager' && !utilizationManagerEnabled) return acc;

    if (
      filterKey === ONLY_OTHER_ORGANIZATIONS &&
      (value as FilterValueBoolean).value &&
      profile.isProviderForClientManagedAndProvidedGroupType(tab?.groupType ?? '')
    ) {
      acc[filterKey] = value;
      return acc;
    }

    if (
      filterKey === REMOVED_FROM_CENSUS &&
      (value as FilterValueBoolean).value &&
      profile.actingClient.managesAndProvidesGroupType(tab?.groupType ?? '')
    ) {
      acc[filterKey] = value;
      return acc;
    }

    if (value[0]?.type !== 'Group::RehabFacility' && !(value instanceof FilterValueBoolean)) {
      acc[filterKey] = value;
      return acc;
    }

    const filterKeyIsCurrentProviderTab = filterKey === camelCase(tab?.value ?? '');

    switch (tab?.patientState) {
      case PatientState.CURRENT:
      case undefined:
        if (filterKeyIsCurrentProviderTab || filterKey === STATUS_OUT_OF_DATE) {
          acc[filterKey] = value;
        }

        return acc;
      case PatientState.LATEST:
      case PatientState.CONTINUED:
        return acc;
      default:
        return acc;
    }
  }, {});
}

type SortOption = {
  attributeName: string;
  direction: SortOrder;
  rehabStateApiName: RehabStateApiName;
  label: string;
  visible: boolean;
};

export const portfolioLaneSortingOptions = (
  rehabState: RehabState,
  showSortByProjectedDischargeDate: boolean,
  translate: TranslateFnType
): SortOption[] =>
  [
    {
      attributeName: 'lengthOfStay',
      direction: SortOrder.DESC,
      rehabStateApiName: rehabState.apiName,
      label: 'Length of Stay (Highest)',
      visible: !rehabState.queue,
    },
    {
      attributeName: 'lengthOfStay',
      direction: SortOrder.ASC,
      rehabStateApiName: rehabState.apiName,
      label: 'Length of Stay (Lowest)',
      visible: !rehabState.queue,
    },
    {
      attributeName: 'patientName',
      direction: SortOrder.ASC,
      rehabStateApiName: rehabState.apiName,
      label: 'Patient Name (A-Z)',
      visible: true,
    },
    {
      attributeName: 'patientName',
      direction: SortOrder.DESC,
      rehabStateApiName: rehabState.apiName,
      label: 'Patient Name (Z-A)',
      visible: true,
    },
    {
      attributeName: 'projectedDischargeDate',
      direction: SortOrder.ASC,
      rehabStateApiName: rehabState.apiName,
      label: `Upcoming Projected ${translate('Discharge')} Date`,
      visible: showSortByProjectedDischargeDate,
    },
  ].filter(({ visible }) => visible);
